/**
 * Life tab hooks — real-time countdown (1s), milestones, achievements, settings.
 * Performance: useRef-based RAF loop, only re-renders once per second.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@turnifly/shared/stores'
import {
  fetchFamousMilestones,
  fetchLifeAchievements,
  updateLifeSettings,
  fetchYearStats,
  type YearStats,
} from '@turnifly/shared/api'
import {
  calcYearCountdown,
  calcYearProgress,
  calcMonthProgress,
  calcWeekProgress,
  calcDayProgress,
  calcLifeCountdown,
  type YearCountdown,
  type LifeCountdown,
} from '@turnifly/shared/utils'

// ── Real-time countdown state ──

export interface TimeState {
  yearCountdown: YearCountdown
  lifeCountdown: LifeCountdown | null
  yearProgress: number
  monthProgress: number
  weekProgress: number
  dayProgress: number
}

export function useRealtimeCountdown(
  timezone: string,
  birthDate: string | null | undefined,
  lifeExpectancy: number,
): TimeState {
  const compute = useCallback((): TimeState => ({
    yearCountdown: calcYearCountdown(timezone),
    lifeCountdown: birthDate ? calcLifeCountdown(birthDate, lifeExpectancy) : null,
    yearProgress: calcYearProgress(timezone),
    monthProgress: calcMonthProgress(timezone),
    weekProgress: calcWeekProgress(timezone),
    dayProgress: calcDayProgress(timezone),
  }), [timezone, birthDate, lifeExpectancy])

  const [state, setState] = useState<TimeState>(compute)
  const rafRef = useRef<number | null>(null)
  const lastSecRef = useRef(-1)

  useEffect(() => {
    const tick = (): void => {
      const sec = Math.floor(Date.now() / 1000)
      if (sec !== lastSecRef.current) {
        lastSecRef.current = sec
        setState(compute())
      }
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    }
  }, [compute])

  return state
}

// ── Famous milestones (immutable, fetch once) ──

export function useFamousMilestones() {
  return useQuery({
    queryKey: ['life', 'famous-milestones'],
    queryFn: fetchFamousMilestones,
    staleTime: Infinity,
  })
}

// ── User achievements (goals + streaks + challenges) ──

export function useLifeAchievements() {
  const userId = useAuthStore((s) => s.user?.id)
  const birthDate = useAuthStore((s) => s.user?.birth_date)

  return useQuery({
    queryKey: ['life', 'achievements', userId],
    queryFn: () => fetchLifeAchievements(userId!, birthDate!),
    enabled: !!userId && !!birthDate,
    staleTime: 5 * 60_000,
  })
}

// ── Year stats for detail sheet ──

export function useYearStats(calendarYear: number) {
  const userId = useAuthStore((s) => s.user?.id)

  return useQuery<YearStats>({
    queryKey: ['life', 'year-stats', userId, calendarYear],
    queryFn: () => fetchYearStats(userId!, calendarYear),
    enabled: !!userId,
    staleTime: 5 * 60_000,
  })
}

// ── Update life settings ──

export function useUpdateLifeSettings() {
  const user = useAuthStore((s) => s.user)
  const setUser = useAuthStore((s) => s.setUser)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (settings: { birth_date?: string; life_expectancy?: number }) =>
      updateLifeSettings(user!.id, settings),
    onSuccess: (_data, variables) => {
      if (user) setUser({ ...user, ...variables })
      queryClient.invalidateQueries({ queryKey: ['life'] })
    },
  })
}
